'use client';

import { useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import CollectionEmbed from '@/components/ui/CollectionEmbed';
import SectionRenderer from '@/components/page-sections/SectionRenderer';
import { parseSections, type SectionConfig } from '@shared/lib/page-sections/types';
import { sanitizeCmsHtml } from '@/lib/sanitize-html';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:4000';

type CollectionEmbedRow = {
  id: string;
  collectionId: string;
  displayType: 'CAROUSEL' | 'GRID' | 'LIST';
  productLimit: number;
  title?: string | null;
  showTitle: boolean;
  sortOrder: number;
};

type Page = {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string | null;
  featuredImage?: string | null;
  publishedAt?: string | null;
  collectionEmbeds?: CollectionEmbedRow[];
  /** New section-based layout. When present, takes precedence over `content`+`collectionEmbeds`. */
  sectionsJson?: unknown;
};

type CmsPageSectionProps = {
  slug: string;
  kind?: 'PAGE' | 'BLOG';
  showHeader?: boolean;
  /** When false, collection embeds are not rendered (e.g. legal pages use body HTML only). Default true. */
  showCollectionEmbeds?: boolean;
  /** Privacy / Terms style: breadcrumb, navy title, divider, tuned body typography. */
  variant?: 'default' | 'legal';
  className?: string;
};

/**
 * Typography for a policy document.
 *
 * The rhythm is the point: a paragraph gap wide enough to separate blocks
 * without floating them apart, and a heading that sits far from the section
 * above it and close to the text it introduces, so the grouping is legible
 * before a word is read. The previous scope gave every paragraph the same
 * 16px gap as the line height, which packed the page into one grey slab.
 *
 * These pages carry a single h1, rendered by the header below, so the body
 * starts at h2. See scripts/db/tidy-legal-page-html.ts, which flattens the
 * stored HTML to match.
 */
const legalBodyScope = [
  'text-[1.0625rem] leading-[1.75] text-text-secondary',
  // Nothing pushes the body away from the divider above it.
  '[&>*:first-child]:!mt-0',
  '[&_p]:mb-6 [&_p]:last:mb-0',
  '[&_h2]:mt-14 [&_h2]:mb-3 [&_h2]:text-[1.375rem] [&_h2]:font-bold [&_h2]:leading-snug',
  '[&_h2]:tracking-tight [&_h2]:text-text-primary',
  '[&_h3]:mt-10 [&_h3]:mb-2 [&_h3]:text-[1.0625rem] [&_h3]:font-bold [&_h3]:text-text-primary',
  // A stray h1 in the body would compete with the page title.
  '[&_h1]:mt-14 [&_h1]:mb-3 [&_h1]:text-[1.375rem] [&_h1]:font-bold [&_h1]:text-text-primary',
  '[&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:mb-2 [&_li]:pl-1 [&_li]:last:mb-0',
  '[&_strong]:font-semibold [&_strong]:text-text-primary',
  // Underline sits clear of the descenders and firms up on hover.
  '[&_a]:font-medium [&_a]:text-blue-60 [&_a]:underline [&_a]:underline-offset-4',
  '[&_a]:decoration-blue-60/40 [&_a]:transition-colors hover:[&_a]:decoration-blue-60',
  '[&_hr]:my-12 [&_hr]:border-neutral-10',
  '[&_blockquote]:my-6 [&_blockquote]:border-l-2 [&_blockquote]:border-neutral-15',
  '[&_blockquote]:pl-5 [&_blockquote]:text-text-secondary',
  '[&_table]:my-6 [&_table]:w-full [&_table]:text-left',
  '[&_th]:border-b [&_th]:border-neutral-10 [&_th]:pb-2 [&_th]:pr-4 [&_th]:font-semibold [&_th]:text-text-primary',
  '[&_td]:border-b [&_td]:border-neutral-10/60 [&_td]:py-2 [&_td]:pr-4 [&_td]:align-top',
].join(' ');

export default function CmsPageSection({
  slug,
  kind = 'PAGE',
  showHeader = true,
  showCollectionEmbeds = true,
  variant = 'default',
  className = '',
}: CmsPageSectionProps) {
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${API_BASE}/api/pages/slug/${slug}?kind=${encodeURIComponent(kind)}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) {
            setPage(null);
            setError(res.status === 404 ? 'Page not found' : 'Failed to load page');
          }
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          if (data?.success && data?.page) setPage(data.page);
          else setError(data?.error || 'Failed to load page');
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load page');
          setPage(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug, kind]);

  const sortedEmbeds = useMemo(() => {
    return [...(page?.collectionEmbeds || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [page?.collectionEmbeds]);

  const sections: SectionConfig[] = useMemo(() => {
    return parseSections(page?.sectionsJson);
  }, [page?.sectionsJson]);
  const useSections = sections.length > 0;

  if (loading && variant === 'legal') {
    return (
      <section className={className} aria-busy="true">
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-48 rounded bg-neutral-10" />
          <div className="h-10 max-w-md rounded bg-neutral-10 w-[min(100%,28rem)]" />
          <div className="h-px w-full bg-neutral-10" />
          <div className="space-y-2 pt-4">
            <div className="h-4 w-full rounded bg-neutral-10" />
            <div className="h-4 w-full rounded bg-neutral-10" />
            <div className="h-4 w-2/3 rounded bg-neutral-10" />
          </div>
        </div>
      </section>
    );
  }
  if (loading) return null;

  if (error || !page) {
    if (variant === 'legal') {
      return (
        <section className={className}>
          <p className="text-body-16-desktop text-text-secondary">
            {error === 'Page not found' ? 'This page is not available yet.' : 'We could not load this page. Please try again later.'}
          </p>
        </section>
      );
    }
    return null;
  }

  const isLegal = variant === 'legal';

  return (
    <section className={className}>
      {isLegal ? (
        <Breadcrumbs items={[{ label: page.title }]} className="mb-4" />
      ) : null}

      {showHeader ? (
        <header className={isLegal ? 'mb-7' : 'mb-8'}>
          <h1
            className={
              isLegal
                ? 'text-[2rem] font-bold leading-[1.15] tracking-tight text-text-primary sm:text-[2.5rem]'
                : 'mb-4 text-4xl font-bold text-slate-900 md:text-5xl'
            }
          >
            {page.title}
          </h1>
          {isLegal && page.excerpt ? (
            <p className="mt-3 text-[1.0625rem] leading-relaxed text-text-secondary">{page.excerpt}</p>
          ) : null}
          {!isLegal && page.excerpt ? <p className="text-xl leading-relaxed text-slate-600">{page.excerpt}</p> : null}
        </header>
      ) : null}

      {isLegal ? <div className="border-t border-neutral-10" aria-hidden="true" /> : null}

      {useSections ? (
        // New section-based layout, render through the section component library.
        <SectionRenderer sections={sections} />
      ) : (
        <div className={isLegal ? 'max-w-none pt-9' : 'prose prose-slate max-w-none'}>
          {/* The scope goes on the element holding the HTML, not a wrapper
              around it, so its first-child reset reaches the first heading
              rather than the wrapper div. */}
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(page.content) }}
            className={isLegal ? `page-content ${legalBodyScope}` : 'page-content'}
          />

          {showCollectionEmbeds
            ? sortedEmbeds.map((embed) => (
                <CollectionEmbed
                  key={embed.id}
                  collectionId={embed.collectionId}
                  displayType={embed.displayType}
                  productLimit={typeof embed.productLimit === 'number' ? embed.productLimit : Number(embed.productLimit)}
                  title={embed.title}
                  showTitle={embed.showTitle}
                />
              ))
            : null}
        </div>
      )}
    </section>
  );
}
